import React, { useState } from 'react';
import { FileText, Shield, Clock, AlertTriangle, ShieldCheck, Layers, Gem, Star, DollarSign, CalendarDays, Calendar, Calendar1, Crown, ChartPie } from 'lucide-react';
import { StatsCard } from './components/StatsCard';
import { RegionMetric } from './components/MockInsuranceData';
import MonthlyChart from './components/MonthlyChart';
import { useQuery } from '@tanstack/react-query';
import { getBranchPerformance, getInsurerDashboardMetricsSummary, getPolicyDistribution, getSingleInsurerDashboardMetrics, getTeamDashboardMetrics } from '../../../../api/requests';
import { ITeamAdminMetrics } from '../../../../models/generalMetrics/IGeneralMetrics';
import { ITeamMetricsDTO } from '../../../../resources/interfaces';
import { SelectedDateRange } from '../../../../components/dashboard/date-range-selector/datePicker';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../../states/slices/resourceSlice';
import { faker } from '@faker-js/faker';
import useUserAccount from '../../../../hooks/userAccountHook';
import { YearRangeSelector } from '../../../../components/dashboard/date-range-selector/RangeSelector';
import BranchPerformance from './components/BranchPerformance';
import VehicleVerificationMonthlyTrendsChart from '../../../shared/vehicle-verification/VerificationMonthlyTrendsChart';
import useVehicleVerification from '../../../../hooks/vehicleInspectionHook';
import { formatCurrencyAmount } from '../../../../utils/miscFunctions';
import PremiumFinancingMonthlyTrendsChart from '../../../shared/premiumFinancing/MonthlyTrendsChart';
import { faBullseye } from '@fortawesome/free-solid-svg-icons';
import usePremiumFinancing from '../../../../hooks/premiumFinancingHook';
import VerificationMonthlyTrendsChart from '../../../shared/vehicle-verification/VerificationMonthlyTrendsChart';

interface InsuranceAdminDashboardProps {
  userName?: string;
  isCompany?: boolean;
  id?: number;
}


const InsurerManagementDashboard: React.FC<InsuranceAdminDashboardProps> = ({ userName, isCompany = false, id }) => {
  const { fetchVehicleVerifiactionMetrics } = useVehicleVerification();
  const [metricsSummary, setMetricsSummary] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1
  const { user } = useUserAccount()
  const companyId = user?.company?.id
  const [pieData, setPieData] = useState<any>()
  const [insurerMetrics, setInsurerMetrics] = useState<any>()
  const [insurerBranchPerformance, setInsurerBranchPerformance] = useState<any>()
  const { fetchPremiumFinancingMetrics } = usePremiumFinancing();
  const currentDate = new Date();
  const startDate = moment(currentDate).format('YYYY-MM-DD');

  const [selectedDateRange, setSelectedDateRange] = useState<SelectedDateRange>({
    from: '',
    to: ''
  });
  const [splineChartRange, setSplineChartRange] = useState({
    quarter: 'Q1',
    year: moment().format('YYYY')
  })
  const [generalMetrics, setGeneralMetrics] = useState<ITeamAdminMetrics>({
    mom: {
      count: {
        JANUARY: 0,
        FEBRUARY: 0,
        MARCH: 0,
        APRIL: 0,
        MAY: 0,
        JUNE: 0,
        JULY: 0,
        AUGUST: 0,
        SEPTEMBER: 0,
        OCTOBER: 0,
        NOVEMBER: 0,
        DECEMBER: 0,
      },
      sales: {
        JANUARY: 0,
        FEBRUARY: 0,
        MARCH: 0,
        APRIL: 0,
        MAY: 0,
        JUNE: 0,
        JULY: 0,
        AUGUST: 0,
        SEPTEMBER: 0,
        OCTOBER: 0,
        NOVEMBER: 0,
        DECEMBER: 0,
      }
    },
    sales: {
      this_month: 0,
      today: 0,
      this_week: 0,
      this_year: 0
    },
    products: {
      'Auto Insurance Sales': 0,
      'Home Insurance Sales': 0
    },
    salesMix: {
      amount: {
        comprehensive_90: 0,
        comprehensive_85: 0,
        comprehensive_100: 0,
        third_party_ft: 0,
        third_party: 0
      },
      count: {
        comprehensive_90: 0,
        comprehensive_85: 0,
        comprehensive_100: 0,
        third_party_ft: 0,
        third_party: 0
      }
    },
    signups: {
      categories: {
        comprehensive: 0,
        third_party: 0
      }

    }
  });


  useQuery({ queryKey: ['getPerformance',], queryFn: () => getPerformance() })
  useQuery({ queryKey: ['generalMetricsProductSales', generalMetrics], queryFn: () => generalMetricsProductSales() });
  useQuery({ queryKey: ['getTeamMetricsData', selectedDateRange, splineChartRange, countryId], queryFn: () => getTeamMetricsData() });
  useQuery({ queryKey: ['generalMetricsSalesMix', generalMetrics], queryFn: () => generalMetricsSalesMix() });
  useQuery({ queryKey: ['getInsurerMetrics'], queryFn: () => getInsurerMetrics() })
  useQuery({ queryKey: ['getSingleInsurerMetrics'], queryFn: () => getSingleInsurerMetrics() })
  useQuery({ queryKey: ['getPieChartData'], queryFn: () => getPieChartData() })

  const { data: vehicleVerificationMetrics, isLoading: vehicleVerificationMetricsLoading } = useQuery({
    queryKey: ['fetchVehicleVerificationMetrics', companyId, selectedDateRange],
    queryFn: () => fetchVehicleVerifiactionMetrics(companyId, selectedDateRange?.from, selectedDateRange?.to),
    enabled: !!companyId
  })

  const { data: premiumFinancingMetrics, isLoading: premiumFinancingMetricsLoading } = useQuery({
    queryKey: ['fetchPremiumFinancingMetrics', companyId, selectedDateRange],
    queryFn: () => fetchPremiumFinancingMetrics(companyId, selectedDateRange?.from, selectedDateRange?.to),
    enabled: !!companyId
  })


  const getPerformance = () => {
    getBranchPerformance(startDate, startDate)
      .then((response: any) => {
        setInsurerBranchPerformance(response?.data?.data)
      })
      .catch((error: Error) => {
        console.log("error", error)
      })
      .finally(() => { })
  }

  const handleSplineChartDateRangeChange = (dateRange: { quarter: string; year: string }) => {
    setSplineChartRange(dateRange);
  };

  const [salesMix, setSalesMix] = useState({
    amount: {
      "Auto Com": 0,
      'Auto TP': 0
    },
    count: {
      "Auto Com": 0,
      'Auto TP': 0
    }
  });

  const [productSales, setproductsales] = useState({
    'Auto Policies': 0,
    'Home Policies': 0
  });

  const generalMetricsProductSales = () => {
    if (generalMetrics.products) {
      setproductsales({
        ...productSales,
        'Auto Policies': generalMetrics.products['Auto Insurance Sales'] ?? 0,
        'Home Policies': generalMetrics.products['Home Insurance Sales'] ?? 0
      });
    }

  }

  const generalMetricsSalesMix = () => {
    if (generalMetrics.salesMix) {
      setSalesMix({
        ...salesMix,
        amount: {
          "Auto Com":
            generalMetrics?.salesMix?.amount?.comprehensive_100 +
            generalMetrics?.salesMix?.amount?.comprehensive_90 +
            generalMetrics?.salesMix?.amount?.comprehensive_85,
          'Auto TP':
            generalMetrics?.salesMix?.amount?.third_party +
            generalMetrics?.salesMix?.amount?.third_party_ft
        },
        count: {
          "Auto Com":
            generalMetrics?.salesMix?.count?.comprehensive_100 +
            generalMetrics?.salesMix?.count?.comprehensive_90 +
            generalMetrics?.salesMix?.count?.comprehensive_85,
          'Auto TP':
            generalMetrics?.salesMix?.count?.third_party +
            generalMetrics?.salesMix?.count?.third_party_ft
        }
      });
    }
  }

  const getTeamMetricsData = () => {
    setLoading(true);

    const params: ITeamMetricsDTO = { ...selectedDateRange, year: splineChartRange.year }
    getTeamDashboardMetrics(countryId, params)
      .then((res) => setGeneralMetrics(res))
      .catch((error: Error) => { })
      .finally(() => {
        setLoading(false);
      });
  };

  const getPieChartData = async () => {
    await getPolicyDistribution(id)
      .then((res: any) => {
        setPieData(res.data)
      })
      .catch((ror: Error) => { console.log("ror", ror) })
      .finally(() => setLoading(false))

  }

  const getInsurerMetrics = async () => {
    await getInsurerDashboardMetricsSummary(id)
      .then((res: any) => {
        setInsurerMetrics(res.data)
      })
      .catch((ror: Error) => {
        console.log(ror)
      })
      .finally(() => { })
  }

  const getSingleInsurerMetrics = async () => {
    await getSingleInsurerDashboardMetrics(id)
      .then((res) => {
      })
      .catch((ror: Error) => console.log(ror))
      .finally(() => { })
  }

  // Quick stats for insurance admin dashboard
  const insuranceStats = [
    {
      title: 'Active Policies',
      value: insurerMetrics?.totalActivePolicies ? insurerMetrics?.totalActivePolicies : '-',
      change: ``,
      changeType: 'positive' as const,
      icon: ShieldCheck,
      color: 'blue' as const
    },
    {
      title: 'Comprehensive',
      value: generalMetrics?.salesMix?.count ? generalMetrics?.salesMix?.count?.comprehensive_100 +
        generalMetrics?.salesMix?.count?.comprehensive_90 +
        generalMetrics?.salesMix?.count?.comprehensive_85 : '-',
      change: '',
      changeType: 'neutral' as const,
      icon: Crown,
      color: 'green' as const
    },
    {
      title: 'Third Party Fire/Theft',
      value: generalMetrics?.salesMix?.count?.third_party_ft ? generalMetrics?.salesMix?.count?.third_party_ft : '-',
      change: '',
      changeType: 'positive' as const,
      icon: Gem,
      color: 'yellow' as const
    },
    {
      title: 'Third Party',
      value: generalMetrics?.salesMix?.count?.third_party ? generalMetrics?.salesMix?.count?.third_party : '-',
      change: '',
      changeType: 'positive' as const,
      icon: Star,
      color: 'orange' as const
    },
  ];

  return (
    <div className="space-y-8">
      {!isCompany && <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-darkBg-mainText mb-2">
          Insurance Dashboard {userName ? `- Welcome, ${userName}` : ''}
        </h1>
        <p className="text-gray-600 dark:text-darkBg-mainText">Monitor policies, claims, and underwriting performance</p>
      </div>}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {insuranceStats.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </div>

      {/* Key Performance Indicators */}
      <div className="bg-white shadow rounded-2xl p-6 dark:bg-darkBg dark:border dark:border-darkBg-border overflow-hidden">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText">Revenue Performance Metrics</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Premium KPI */}
          <KeyPerformanceIndicatorItem title={'Premium Revenue'}
            value={insurerMetrics?.totalPremiumAmount ? insurerMetrics?.totalPremiumAmount.toFixed(2) : '-'}
            icon={<ChartPie className="h-5 w-5" />}
            iconClasses='bg-blue-50 text-blue-600'
          />

          {/* Claims Ratio KPI */}
          <KeyPerformanceIndicatorItem title={'This Month'}
            value={generalMetrics?.sales?.this_month ? generalMetrics?.sales?.this_month.toFixed(2) : '-'}
            icon={<CalendarDays className="h-5 w-5" />}
            iconClasses='bg-green-50 text-green-600'
          />

          {/* Customer Satisfaction KPI */}
          <KeyPerformanceIndicatorItem title={'This Week'}
            value={generalMetrics?.sales?.this_week ? generalMetrics?.sales?.this_week.toFixed(2) : '-'}
            icon={<Calendar className="h-5 w-5" />}
            iconClasses='bg-yellow-50 text-yellow-600'
          />

          {/* Retention Rate KPI */}
          <KeyPerformanceIndicatorItem title={'Today'}
            value={generalMetrics?.sales?.today ? generalMetrics?.sales?.today.toFixed(2) : '-'}
            icon={<Calendar1 className="h-5 w-5" />}
            iconClasses='bg-orange-50 text-orange-600'
          />
        </div>
      </div>


      {/* Charts Grid */}

      <div className='flex py-2 px-8 justify-end'>
        <YearRangeSelector
          onPoliciesYearChange={(year: string) => {
            handleSplineChartDateRangeChange({ ...splineChartRange, year: year })
          }}
        />
      </div>

      <MonthlyChart
        metricsSummary={metricsSummary ?? {}}
        labels={pieData?.labels ?? []}
        policyCount={pieData?.policyCount ?? []}
        premium={pieData?.premium ?? []}
        productData={generalMetrics?.salesMix ?? {}}
        trendData={generalMetrics?.mom?.sales ?? {}}
        teamMetrics={generalMetrics ?? {}}
        loading={loading}
        chartOption1='Amount'
        chartOption2='Volume'
      />

      <div className="w-full space-y-4">
        <BranchPerformance
          bestPerformingBranch={insurerBranchPerformance?.bestPerformingBranch ? insurerBranchPerformance?.bestPerformingBranch : '-'}
          branchMetrics={insurerBranchPerformance?.branchMetrics ? insurerBranchPerformance?.branchMetrics : []}
          companyRevenueTarget={insurerBranchPerformance?.companyRevenueTarget ? insurerBranchPerformance?.companyRevenueTarget : '-'}
          totalRevenue={insurerBranchPerformance?.totalRevenue} />

        <VerificationMonthlyTrendsChart
          isLoading={vehicleVerificationMetricsLoading}
          monthlyTrend={vehicleVerificationMetrics?.monthlyTrend || []}
          protectionTypeDistribution={vehicleVerificationMetrics?.protectionTypeDistribution || {}}
          statusDistribution={vehicleVerificationMetrics?.statusDistribution || {}}
          title="Verification Overview"
          description="Monthly trends and distribution data"
        />

        <PremiumFinancingMonthlyTrendsChart
          isLoading={premiumFinancingMetricsLoading}
          data={premiumFinancingMetrics?.monthlyTrend || []}
          title="Premium Financing Monthly Trends"
          description="Showing count data by month"
        />
      </div>
    </div>
  );
};

export default InsurerManagementDashboard;

export const generateRegionMetrics = (): RegionMetric[] => {
  const regions = ['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West', 'Northwest', 'Central'];

  return regions.map(region => ({
    region,
    policies: faker.number.int({ min: 2000, max: 10000 }),
    premium: faker.number.int({ min: 1000000, max: 5000000 }),
    claims: faker.number.int({ min: 100, max: 2000 }),
    growth: faker.number.float({ min: -2, max: 15 })
  }));
};

interface keyPerformanceIndicatorItemProps {
  title: string;
  value: number | string;
  children?: JSX.Element | undefined;
  icon: JSX.Element;
  iconClasses?: string;
}
export const KeyPerformanceIndicatorItem = ({ title, value, children, icon, iconClasses }: keyPerformanceIndicatorItemProps) => {
  return (
    <div className="bg-gray-50 p-4 rounded-2xl dark:bg-darkBg dark:border dark:border-darkBg-border overflow-hidden">
      <div className='flex items-center gap-3'>
        {/* <DollarSign /> */}
        <div className={`${iconClasses} w-8 h-8 rounded-md flex items-center justify-center`}>
          {icon}
        </div>

        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-darkBg-mainText">{title}{" (GHS)"}</p>
          <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-darkBg-mainText">{value}</p>
        </div>
      </div>


      {children && <div className="">
        {children}
      </div>}
    </div>
  )
}